import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Instagram, Facebook } from 'lucide-react';

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 448 512" fill="currentColor" className={className}>
    <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z" />
  </svg>
);
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { ScrollImageSection } from '@/components/ScrollImageSection';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import SEO from '@/components/SEO';
import ImageWithSEO from '@/components/ImageWithSEO';
import homeStory1 from '@/assets/home-story-1.jpg';
import homeStory2 from '@/assets/home-story-2.jpg';
import homeStory3 from '@/assets/home-story-3.jpg';
import drinksSection from '@/assets/drinks-section.jpg';
import menuBannerImage from '@/assets/menu-banner.jpg';
import ctaSection from '@/assets/cta-section.jpg';

const Home = () => {
  const { t } = useLanguage();
  const { socialMedia } = useSiteSettings();
  const [settings, setSettings] = useState<any>({
    hero: {},
    story: {},
    home_bar: {},
    drinks: {},
    explore_menu: {},
    social: {},
    private_dining: {},
    cta: {}
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('home_settings')
        .select('*');

      if (error) throw error;

      if (data && data.length > 0) {
        const settingsObj: any = {};
        data.forEach((row) => {
          settingsObj[row.section] = row.data;
        });

        setSettings({
          hero: settingsObj.hero || {},
          story: settingsObj.story || {},
          home_bar: settingsObj.home_bar || {},
          drinks: settingsObj.drinks || {},
          explore_menu: settingsObj.explore_menu || {},
          social: settingsObj.social || {},
          private_dining: settingsObj.private_dining || {},
          cta: settingsObj.cta || {}
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load homepage content');
    }
  };

  const defaultHero = {
    title: 'Capital District',
    subtitle: 'Your Neighborhood American Bar & Restaurant'
  };

  const defaultStory = {
    title: 'Where Good Times Meet Great Food',
    text: 'Capital District brings the spirit of classic American dining to your neighborhood. From signature cocktails to elevated bar fare, we create memorable experiences for every occasion.'
  };

  const defaultDrinks = {
    title: 'Craft Cocktails & Cold Brews',
    description: 'From hand-crafted cocktails to local craft beers, our bar has something for everyone. Join us for happy hour or late-night drinks.'
  };

  const heroContent = settings?.hero?.title ? settings.hero : defaultHero;
  const storyContent = settings?.story?.title ? settings.story : defaultStory;
  const drinksContent = settings?.drinks?.title ? settings.drinks : defaultDrinks;

  return (
    <main className="bg-background">
      <SEO />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {settings?.hero?.image_url ? (
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${settings.hero.image_url})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(220,20%,8%)] via-[hsl(220,18%,12%)] to-[hsl(220,20%,8%)]"></div>
        )}
        
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold text-white mb-6 tracking-tight">
            {heroContent.title}
          </h1>
          {heroContent.subtitle && (
            <p className="text-xl md:text-2xl text-white/70 mb-10 max-w-2xl mx-auto">
              {heroContent.subtitle}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button size="lg" className="text-lg px-8 py-6 min-w-[200px]" asChild>
              <Link to="/menu">{t('viewMenu')}</Link>
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 py-6 min-w-[200px] border-primary/50 hover:bg-primary/10" asChild>
              <Link to="/reservations">{t('reservations')}</Link>
            </Button>
          </div>
        </div>

        {/* Decorative bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent"></div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-muted/40 to-background">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12 md:mb-20">
            <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-6">
              {storyContent.title}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              {storyContent.text}
            </p>
          </div>

          {/* Photo Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="overflow-hidden rounded-xl shadow-2xl group">
              <ImageWithSEO
                src={settings?.story?.image1_url || homeStory1}
                alt="Capital District Social restaurant ambiance and dining atmosphere in Tallahassee FL"
                context="Home - Restaurant Ambiance"
                keywords={['restaurant ambiance', 'Tallahassee dining', 'Capital District Social']}
                loading="lazy"
                decoding="async"
                className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-xl shadow-2xl group">
              <ImageWithSEO
                src={settings?.story?.image2_url || homeStory2}
                alt="Delicious food presentation at Capital District Social American bar and restaurant"
                context="Home - Food Presentation"
                keywords={['food presentation', 'American cuisine', 'bar food']}
                loading="lazy"
                decoding="async"
                className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-xl shadow-2xl group">
              <ImageWithSEO
                src={settings?.story?.image3_url || homeStory3}
                alt="Capital District Social bar area with craft cocktails and cold drinks"
                context="Home - Bar Area"
                keywords={['bar area', 'craft cocktails', 'Tallahassee bar']}
                loading="lazy"
                decoding="async"
                className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/menu">{t('viewMenu')}</Link>
            </Button>
            <Button size="lg" variant="secondary" asChild>
              <Link to="/about">{t('aboutNav')}</Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/reservations">{t('reserveTable')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* About Us Preview Section */}
      <section className="py-16 md:py-24 px-4 bg-muted/30 border-b border-border">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6">
                {settings?.home_bar?.title || t('homeBarTitle')}
              </h2>
              <p className="text-lg text-muted-foreground mb-4 leading-relaxed">
                {settings?.home_bar?.text1 || t('homeBarText1')}
              </p>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                {settings?.home_bar?.text2 || t('homeBarText2')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" asChild>
                  <Link to="/about">{t('learnMore')}</Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/contact">{t('contactNav')}</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <ImageWithSEO
                src={settings?.home_bar?.image_url || homeStory1}
                alt="Capital District Social atmosphere - neighborhood American bar and restaurant in Tallahassee"
                context="Home - About Preview"
                keywords={['restaurant atmosphere', 'American bar', 'Tallahassee restaurant']}
                loading="lazy"
                decoding="async"
                className="rounded-xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-primary/20 rounded-xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Drinks Section */}
      <ScrollImageSection
        imageUrl={settings?.drinks?.image_url || drinksSection}
        minHeight="80vh"
        imageAlt="Craft cocktails and cold brews at Capital District Social bar in Tallahassee FL"
        overlayOpacity={0.4}
      >
        <div className="border-2 border-primary/30 p-8 md:p-16 text-center bg-card/95 backdrop-blur-sm max-w-4xl mx-auto rounded-2xl shadow-2xl">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
            {drinksContent.title}
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
            {drinksContent.description}
          </p>
          <Button size="lg" className="px-10" asChild>
            <Link to="/menu">{t('ourDrinksButton')}</Link>
          </Button>
        </div>
      </ScrollImageSection>

      {/* Menu Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-background to-muted/30 border-b border-border">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="aspect-square overflow-hidden rounded-xl shadow-2xl">
              <ImageWithSEO
                src={settings?.explore_menu?.image_url || menuBannerImage}
                alt="Capital District Social menu featuring burgers, wings, tacos and more - American bar cuisine"
                context="Home - Explore Menu"
                keywords={['restaurant menu', 'burgers', 'wings', 'tacos', 'American cuisine']}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6">
                {settings?.explore_menu?.title || t('exploreMenu')}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {settings?.explore_menu?.description || t('exploreMenuDesc')}
              </p>
              <Button size="lg" className="px-10" asChild>
                <Link to="/menu">{t('viewFullMenu')}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      {settings?.social?.title && (
        <section className="py-16 md:py-24 px-4 border-b border-border bg-gradient-to-b from-muted/40 to-background">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-6">
                {settings.social.title}
              </h2>
              <div className="flex justify-center gap-4 mb-6">
                {socialMedia.instagram && (
                  <a href={socialMedia.instagram} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary text-primary-foreground hover:bg-primary/80 transition-colors shadow-lg"
                    aria-label="Instagram">
                    <Instagram className="w-6 h-6" />
                  </a>
                )}
                {socialMedia.tiktok && (
                  <a href={socialMedia.tiktok} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary text-primary-foreground hover:bg-primary/80 transition-colors shadow-lg"
                    aria-label="TikTok">
                    <TikTokIcon className="w-6 h-6" />
                  </a>
                )}
                {socialMedia.facebook && (
                  <a href={socialMedia.facebook} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary text-primary-foreground hover:bg-primary/80 transition-colors shadow-lg"
                    aria-label="Facebook">
                    <Facebook className="w-6 h-6" />
                  </a>
                )}
              </div>
              {settings.social.description && (
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  {settings.social.description}
                </p>
              )}
            </div>

            {(settings.social.image1_url || settings.social.image2_url || settings.social.image3_url || settings.social.image4_url) && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {settings.social.image1_url && (
                  <div className="aspect-square overflow-hidden rounded-xl group">
                    <ImageWithSEO
                      src={settings.social.image1_url}
                      alt="Capital District Social social media highlight - dining experience"
                      context="Home - Social Media"
                      keywords={['social media', 'restaurant life', 'Tallahassee nightlife']}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      includeStructuredData={false}
                    />
                  </div>
                )}
                {settings.social.image2_url && (
                  <div className="aspect-square overflow-hidden rounded-xl group">
                    <ImageWithSEO
                      src={settings.social.image2_url}
                      alt="Capital District Social events and gatherings"
                      context="Home - Social Media"
                      keywords={['events', 'gatherings', 'bar scene']}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      includeStructuredData={false}
                    />
                  </div>
                )}
                {settings.social.image3_url && (
                  <div className="aspect-square overflow-hidden rounded-xl group">
                    <ImageWithSEO
                      src={settings.social.image3_url}
                      alt="Capital District Social food and drinks highlights"
                      context="Home - Social Media"
                      keywords={['food highlights', 'drinks', 'specials']}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      includeStructuredData={false}
                    />
                  </div>
                )}
                {settings.social.image4_url && (
                  <div className="aspect-square overflow-hidden rounded-xl group">
                    <ImageWithSEO
                      src={settings.social.image4_url}
                      alt="Capital District Social community and game day atmosphere"
                      context="Home - Social Media"
                      keywords={['game day', 'community', 'Tallahassee']}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      includeStructuredData={false}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Private Dining Section */}
      {settings?.private_dining?.title && settings?.private_dining?.image_url && (
        <ScrollImageSection
          imageUrl={settings.private_dining.image_url}
          minHeight="70vh"
          imageAlt="Private dining and event space at Capital District Social Tallahassee"
          overlayOpacity={0.5}
        >
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-6">
              {settings.private_dining.title}
            </h2>
            {settings.private_dining.description && (
              <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed">
                {settings.private_dining.description}
              </p>
            )}
            <Button size="lg" variant="secondary" className="px-10" asChild>
              <Link to="/catering">{t('cateringLink')}</Link>
            </Button>
          </div>
        </ScrollImageSection>
      )}

      {/* Final CTA Section */}
      <ScrollImageSection
        imageUrl={settings?.cta?.image_url || ctaSection}
        minHeight="70vh"
        imageAlt="Capital District Social restaurant atmosphere - American bar and grill in Tallahassee FL"
        overlayOpacity={0.6}
      >
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-6">
            {settings?.cta?.title || 'Ready for a Great Time?'}
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-10 leading-relaxed">
            {settings?.cta?.description || 'Join us for delicious food, amazing drinks, and the best atmosphere in town.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="text-lg px-8 py-6" asChild>
              <Link to="/reservations">{t('reserveTable')}</Link>
            </Button>
            <Button size="lg" variant="secondary" className="text-lg px-8 py-6" asChild>
              <Link to="/menu">{t('viewMenu')}</Link>
            </Button>
          </div>
        </div>
      </ScrollImageSection>
    </main>
  );
};

export default Home;
